import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, TextField, InputAdornment, Chip } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon  from '@mui/icons-material/SendRounded';
import { motion, AnimatePresence } from 'framer-motion';
import { C, F, G, SH } from '../styles/tokens';

// ─── Animated Lion SVG ────────────────────────────────────────────────────────
const LionAvatar = ({ size = 40 }) => (
  <motion.svg
    width={size} height={size} viewBox="0 0 60 60"
    animate={{ y: [0, -3, 0] }}
    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
    style={{ display: 'block', flexShrink: 0 }}
  >
    <motion.circle cx="30" cy="31" r="27" fill="#D97706"
      animate={{ scale: [1, 1.03, 1] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
    />
    <circle cx="30" cy="31" r="22" fill="#F59E0B" />
    <circle cx="30" cy="31" r="18" fill="#FDE68A" />
    <circle cx="13" cy="14" r="7" fill="#D97706" />
    <circle cx="13" cy="14" r="4" fill="#FDE68A" />
    <circle cx="47" cy="14" r="7" fill="#D97706" />
    <circle cx="47" cy="14" r="4" fill="#FDE68A" />
    <circle cx="23" cy="28" r="5" fill="white" />
    <circle cx="37" cy="28" r="5" fill="white" />
    <motion.circle cx="23" cy="28" r="2.5" fill="#1E293B"
      animate={{ scaleY: [1, 0.08, 1] }}
      transition={{ duration: 0.18, repeat: Infinity, repeatDelay: 3.5, ease: 'easeInOut' }}
    />
    <motion.circle cx="37" cy="28" r="2.5" fill="#1E293B"
      animate={{ scaleY: [1, 0.08, 1] }}
      transition={{ duration: 0.18, repeat: Infinity, repeatDelay: 3.5, ease: 'easeInOut' }}
    />
    <circle cx="24.2" cy="26.8" r="1" fill="white" opacity="0.8" />
    <circle cx="38.2" cy="26.8" r="1" fill="white" opacity="0.8" />
    <ellipse cx="30" cy="34" rx="3" ry="2" fill="#D97706" />
    <path d="M26 37.5 Q30 41 34 37.5" stroke="#D97706" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    <line x1="12" y1="33" x2="22" y2="34.5" stroke="#D97706" strokeWidth="1" opacity="0.6" />
    <line x1="12" y1="36" x2="22" y2="36"   stroke="#D97706" strokeWidth="1" opacity="0.6" />
    <line x1="38" y1="34.5" x2="48" y2="33" stroke="#D97706" strokeWidth="1" opacity="0.6" />
    <line x1="38" y1="36"   x2="48" y2="36" stroke="#D97706" strokeWidth="1" opacity="0.6" />
  </motion.svg>
);

// ─── Constants ────────────────────────────────────────────────────────────────
const MOBILE_RE = /^[6-9][0-9]{9}$/;
const EMAIL_RE  = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
  'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka',
  'Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
  'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman & Nicobar Islands','Chandigarh',
  'Dadra & Nagar Haveli and Daman & Diu','Delhi (NCT)',
  'Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry',
];

const REGISTRATION_TYPES = [
  'Private Limited Company',
  'One Person Company (OPC)',
  'Limited Liability Partnership (LLP)',
  'Partnership Firm',
  'Sole Proprietorship',
  'Section 8 Company (NGO)',
  'GST Registration',
  'Tax Filing / ITR',
  'Accounting & Bookkeeping',
  'Other',
];

const AUTH_CAPITAL_OPTIONS = ['₹1 Lakh', '₹5 Lakhs', '₹10 Lakhs', '₹25 Lakhs', '₹50 Lakhs+'];

const QUICK_ACTIONS = ['Company Registration', 'GST Filing', 'Income Tax', 'Accounting'];

// ─── Steps ────────────────────────────────────────────────────────────────────
const STEP = {
  IDLE: 'idle', NAME: 'name', EMAIL: 'email', MOBILE: 'mobile',
  STATE: 'state', TYPE: 'type', AUTH_CAPITAL: 'auth_capital',
  DIRECTORS: 'directors', DONE: 'done',
};

// ─── Q&A rules ────────────────────────────────────────────────────────────────
const QA_RULES = [
  { keywords: ['company','register','registration','incorporate','pvt','llp','opc','startup'],
    reply: `We handle end-to-end company registration — Pvt Ltd, LLP, OPC & more. All MCA filings managed by our experts.\n\nTap **Get Free Consultation** or type yes to begin!` },
  { keywords: ['gst','gstin','gst filing','return'],
    reply: `Complete GST solutions: registration, monthly/quarterly filing, reconciliation & notices handled for you.\n\nTap **Get Free Consultation** or type yes!` },
  { keywords: ['tax','itr','income tax','tds'],
    reply: `ITR for individuals, firms & companies. TDS returns and advance tax too — stress-free!\n\nTap **Get Free Consultation** or type yes!` },
  { keywords: ['account','bookkeeping','books','balance sheet','audit','tally'],
    reply: `Outsourced bookkeeping, P&L, balance sheets & audit support on Tally. Always accurate, always on time.\n\nTap **Get Free Consultation** or type yes!` },
  { keywords: ['price','cost','fee','charge','rate','plan','package'],
    reply: `Pricing is customised to your business size. Type yes and we'll send you a tailored quote within 24 hours!` },
  { keywords: ['contact','reach','call','phone','email','whatsapp','talk'],
    reply: `📱 WhatsApp: +91 93118 17126\n✉️ Email: info@pracco.in\n\nWe typically respond within minutes!` },
  { keywords: ['hello','hi','hey','hii','namaste'],
    reply: `Hi there! 🦁 Great to see you! I'm Pracco Buddy — ask me anything or tap a quick option below.` },
  { keywords: ['time','how long','duration','days','fast','quick'],
    reply: `Company registration: 7–10 working days. GST registration: 3–5 days. We keep you updated every step!` },
  { keywords: ['yes','sure','ok','okay','start','proceed','consultation','help','interested','get free'],
    reply: null },
];

const FALLBACK = `Hmm, I'm not sure about that! 🦁\n\nAsk me about company registration, GST, income tax, or accounting — or tap a quick option below.`;

const GREETING = `Roar! 🦁 I'm Pracco Buddy!\n\nI'm here to help you with:\n• Company Registration\n• GST Filing\n• Income Tax / ITR\n• Accounting & Bookkeeping\n\nWhat can I help you with today?`;

// ─── Typing dots ──────────────────────────────────────────────────────────────
const TypingDot = ({ delay }) => (
  <Box component={motion.div}
    animate={{ y: [0, -5, 0] }}
    transition={{ duration: 0.6, repeat: Infinity, delay }}
    sx={{ width: 7, height: 7, borderRadius: '50%', background: C.textMuted }}
  />
);

// ─── Main component ───────────────────────────────────────────────────────────
const ChatWidget = () => {
  const [open,        setOpen]        = useState(false);
  const [showBubble,  setShowBubble]  = useState(false);
  const [messages,    setMessages]    = useState([{ from: 'bot', text: GREETING, id: 0 }]);
  const [input,       setInput]       = useState('');
  const [typing,      setTyping]      = useState(false);
  const [step,        setStep]        = useState(STEP.IDLE);
  const [userData,    setUserData]    = useState({ name:'', email:'', mobile:'', state:'', type:'', authCapital:'', directors:'' });
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const bubbleTimer = useRef(null);

  // Show greeting bubble after 4s, auto-hide after 10s
  useEffect(() => {
    const show = setTimeout(() => setShowBubble(true), 4000);
    const hide = setTimeout(() => setShowBubble(false), 14000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, typing]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 350); }, [open]);

  const openChat = () => {
    setShowBubble(false);
    setOpen(true);
  };

  const addBot = (text, newStep) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, { from: 'bot', text, id: Date.now() }]);
      if (newStep !== undefined) setStep(newStep);
    }, 800 + Math.random() * 300);
  };

  const addUser = (text) =>
    setMessages(prev => [...prev, { from: 'user', text, id: Date.now() }]);

  const handleChip = (value) => { addUser(value); processInput(value, true); };

  const processInput = (raw, isChip = false) => {
    const text = raw.trim();
    if (!text) return;

    if (step === STEP.NAME) {
      setUserData(d => ({ ...d, name: text }));
      addBot(`Nice to meet you, ${text}! 😊\n\nWhat's your email address?`, STEP.EMAIL);
      return;
    }
    if (step === STEP.EMAIL) {
      if (!EMAIL_RE.test(text)) { addBot(`That doesn't look like a valid email. Please try again.`); return; }
      setUserData(d => ({ ...d, email: text }));
      addBot(`Got it! Now your 10-digit mobile number please.`, STEP.MOBILE);
      return;
    }
    if (step === STEP.MOBILE) {
      if (!MOBILE_RE.test(text)) { addBot(`Please enter a valid 10-digit mobile number starting with 6–9.`); return; }
      setUserData(d => ({ ...d, mobile: text }));
      addBot(`Which state are you registering your business in?\n\nType the state name (e.g. Delhi, Maharashtra).`, STEP.STATE);
      return;
    }
    if (step === STEP.STATE) {
      const match = INDIAN_STATES.find(s => s.toLowerCase().includes(text.toLowerCase()));
      if (!match) { addBot(`I couldn't find that state. Try again (e.g. Delhi, Maharashtra, Karnataka).`); return; }
      setUserData(d => ({ ...d, state: match }));
      addBot(`Great choice! 🎯 Now select your type of registration below.`, STEP.TYPE);
      return;
    }
    if (step === STEP.TYPE) {
      const match = isChip ? text : REGISTRATION_TYPES.find(t => t.toLowerCase().includes(text.toLowerCase()));
      if (!match) { addBot(`Please select or type one of the options shown.`); return; }
      setUserData(d => ({ ...d, type: match }));
      if (match === 'Private Limited Company') {
        addBot(`For a Private Limited Company, what's your desired Authorized Capital?\n\nThis is the maximum share capital your company can issue.`, STEP.AUTH_CAPITAL);
      } else {
        confirmAndFinish({ ...userData, type: match });
      }
      return;
    }
    if (step === STEP.AUTH_CAPITAL) {
      setUserData(d => ({ ...d, authCapital: text }));
      addBot(`How many directors will the company have? (Minimum 2 for Pvt Ltd)`, STEP.DIRECTORS);
      return;
    }
    if (step === STEP.DIRECTORS) {
      const num = parseInt(text, 10);
      if (isNaN(num) || num < 2) { addBot(`A Private Limited Company needs at least 2 directors. Please enter a valid number.`); return; }
      const final = { ...userData, directors: String(num) };
      setUserData(final);
      confirmAndFinish(final);
      return;
    }

    // IDLE Q&A
    const lower = text.toLowerCase();
    for (const rule of QA_RULES) {
      if (rule.keywords.some(k => lower.includes(k))) {
        if (rule.reply === null) {
          addBot(`Let's get you a free consultation! 🎉\n\nFirst, what's your full name?`, STEP.NAME);
        } else {
          addBot(rule.reply);
        }
        return;
      }
    }
    addBot(FALLBACK);
  };

  const confirmAndFinish = (data) => {
    const isPvt = data.type === 'Private Limited Company';
    const summary = [
      `All set! Here's your summary:\n`,
      `👤 Name: ${data.name}`,
      `✉️  Email: ${data.email}`,
      `📱 Mobile: ${data.mobile}`,
      `🏛  State: ${data.state}`,
      `📝 Service: ${data.type}`,
      isPvt && `💰 Auth Capital: ${data.authCapital}`,
      isPvt && `👥 Directors: ${data.directors}`,
      `\nOur team will call you within 24 hours. For faster help, WhatsApp us at +91 93118 17126. Thank you! 🦁🙏`,
    ].filter(Boolean).join('\n');

    fetch('https://formspree.io/f/mnnpwlwq', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        name: data.name, email: data.email, mobile: data.mobile,
        state: data.state, registrationType: data.type,
        ...(isPvt && { authorizedCapital: data.authCapital, numberOfDirectors: data.directors }),
        _source: 'chatbot',
      }),
    }).catch(() => {});

    addBot(summary, STEP.DONE);
  };

  const sendMessage = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    addUser(trimmed);
    setInput('');
    processInput(trimmed);
  };

  const handleKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const getChips = () => {
    if (step === STEP.TYPE)         return REGISTRATION_TYPES;
    if (step === STEP.AUTH_CAPITAL) return AUTH_CAPITAL_OPTIONS;
    if (step === STEP.IDLE)         return QUICK_ACTIONS;
    return [];
  };
  const chips = getChips();

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>

      {/* ── Chat panel ── */}
      <AnimatePresence>
        {open && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.85, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            sx={{
              position: 'absolute', bottom: 76, right: 0,
              width: { xs: 320, sm: 360 }, height: 510,
              borderRadius: '20px', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 24px 80px rgba(15,32,64,0.22), 0 4px 16px rgba(15,32,64,0.10)',
              border: `1px solid ${C.border}`, background: C.white,
            }}
          >
            {/* Header */}
            <Box sx={{ background: G.navyBlue, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <LionAvatar size={44} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: 14, color: C.white, lineHeight: 1.2 }}>
                  Pracco Buddy
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.3 }}>
                  {/* Animated roaring dots */}
                  {[0, 0.2, 0.4].map((d, i) => (
                    <Box key={i} component={motion.div}
                      animate={{ scaleY: [1, 2.2, 1], opacity: [0.6, 1, 0.6] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: d, ease: 'easeInOut' }}
                      sx={{ width: 3, height: 8, borderRadius: 2, background: C.amber, transformOrigin: 'bottom' }}
                    />
                  ))}
                  <Typography sx={{ fontFamily: F.body, fontSize: 11, color: 'rgba(255,255,255,0.7)', ml: 0.4 }}>
                    Roaring
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setOpen(false)} size="small"
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: C.white, background: 'rgba(255,255,255,0.1)' } }}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Messages */}
            <Box sx={{
              flex: 1, overflowY: 'auto', px: 2, py: 2,
              display: 'flex', flexDirection: 'column', gap: 1.2,
              background: C.bg,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { background: C.border, borderRadius: 4 },
            }}>
              {messages.map(msg => (
                <Box key={msg.id}
                  component={motion.div}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  sx={{ display: 'flex', justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start' }}
                >
                  <Box sx={{
                    maxWidth: '82%', px: 1.75, py: 1.1,
                    borderRadius: msg.from === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: msg.from === 'user' ? G.blue : C.white,
                    boxShadow: msg.from === 'user' ? SH.btnBlue : SH.card,
                    border: msg.from === 'bot' ? `1px solid ${C.border}` : 'none',
                  }}>
                    <Typography sx={{ fontFamily: F.body, fontSize: 13.5, lineHeight: 1.65, color: msg.from === 'user' ? C.white : C.text, whiteSpace: 'pre-line' }}>
                      {msg.text.replace(/\*\*(.*?)\*\*/g, '$1')}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {/* Typing indicator */}
              <AnimatePresence>
                {typing && (
                  <Box component={motion.div} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Box sx={{ px: 2, py: 1.4, borderRadius: '16px 16px 16px 4px', background: C.white, border: `1px solid ${C.border}`, boxShadow: SH.card, display: 'flex', alignItems: 'center', gap: 0.7 }}>
                      <TypingDot delay={0} /><TypingDot delay={0.15} /><TypingDot delay={0.3} />
                    </Box>
                  </Box>
                )}
              </AnimatePresence>

              {/* Quick reply chips */}
              {!typing && chips.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                  {chips.map(c => (
                    <Chip key={c} label={c} size="small" onClick={() => handleChip(c)}
                      sx={{
                        fontFamily: F.body, fontSize: 12, cursor: 'pointer',
                        background: C.white, border: `1px solid ${C.blue}`,
                        color: C.blue, fontWeight: 500,
                        '&:hover': { background: C.blueLight },
                        height: 'auto', py: 0.4,
                        '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Get Free Consultation CTA in IDLE */}
              {!typing && step === STEP.IDLE && (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  onClick={() => handleChip('yes')}
                  sx={{
                    mt: 0.5, py: 1.1, px: 2, borderRadius: '12px',
                    background: G.blue, cursor: 'pointer', textAlign: 'center',
                    boxShadow: SH.btnBlue,
                    '&:hover': { background: G.blueHover },
                  }}
                >
                  <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: 13, color: C.white }}>
                    🎯 Get Free Consultation
                  </Typography>
                </Box>
              )}

              <div ref={bottomRef} />
            </Box>

            {/* Input */}
            <Box sx={{ px: 2, py: 1.5, background: C.white, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
              {step !== STEP.DONE && (
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  placeholder={
                    step === STEP.NAME         ? 'Your full name…'         :
                    step === STEP.EMAIL        ? 'Your email address…'     :
                    step === STEP.MOBILE       ? '10-digit mobile number…' :
                    step === STEP.STATE        ? 'State of registration…'  :
                    step === STEP.TYPE         ? 'Or type your choice…'    :
                    step === STEP.AUTH_CAPITAL ? 'Authorized capital…'     :
                    step === STEP.DIRECTORS    ? 'Number of directors…'    :
                    'Ask me anything… 🦁'
                  }
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px', fontFamily: F.body, fontSize: 13.5, background: C.bg,
                      '& fieldset': { borderColor: C.border },
                      '&:hover fieldset': { borderColor: C.blueMid },
                      '&.Mui-focused fieldset': { borderColor: C.blue },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={sendMessage} disabled={!input.trim()} size="small"
                          sx={{
                            background: input.trim() ? G.blue : 'transparent',
                            color: input.trim() ? C.white : C.textLight,
                            width: 32, height: 32, borderRadius: '8px', transition: 'all 0.2s',
                            '&:hover': { background: input.trim() ? G.blueHover : 'transparent' },
                            '&.Mui-disabled': { background: 'transparent', color: C.textLight },
                          }}
                        >
                          <SendRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {step === STEP.DONE && (
                <Box sx={{ textAlign: 'center', py: 0.5 }}>
                  <Typography component="a" href="https://wa.me/919311817126" target="_blank" rel="noopener noreferrer"
                    sx={{ fontFamily: F.body, fontSize: 13, color: C.whatsapp, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    📱 Chat on WhatsApp for faster support
                  </Typography>
                </Box>
              )}
              <Typography sx={{ fontFamily: F.body, fontSize: 10.5, color: C.textLight, textAlign: 'center', mt: 0.8 }}>
                Pracco Buddy · info@pracco.in
              </Typography>
            </Box>
          </Box>
        )}
      </AnimatePresence>

      {/* ── Greeting bubble ── */}
      <AnimatePresence>
        {showBubble && !open && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0,  scale: 1   }}
            exit={{   opacity: 0, x: 20,  scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            sx={{
              position: 'absolute', bottom: 76, right: 0,
              width: 220,
              background: C.white,
              borderRadius: '16px 16px 4px 16px',
              boxShadow: '0 8px 32px rgba(15,32,64,0.18)',
              border: `1px solid ${C.border}`,
              p: 1.75,
            }}
          >
            {/* Dismiss */}
            <IconButton size="small" onClick={() => setShowBubble(false)}
              sx={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, color: C.textLight, '&:hover': { color: C.text } }}>
              <CloseRoundedIcon sx={{ fontSize: 13 }} />
            </IconButton>

            <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: 13, color: C.navy, mb: 0.5 }}>
              👋 How can I help you?
            </Typography>
            <Typography sx={{ fontFamily: F.body, fontSize: 12, color: C.textMid, mb: 1.2, lineHeight: 1.5 }}>
              I'm Pracco Buddy! Ask me about company registration, GST, taxes & more.
            </Typography>

            {/* Mini quick actions */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
              {['Register Company', 'GST Help', 'Tax Filing'].map(label => (
                <Box key={label} onClick={openChat}
                  sx={{
                    px: 1, py: 0.4, borderRadius: '8px',
                    background: C.blueLight, border: `1px solid ${C.blue}20`,
                    cursor: 'pointer',
                    '&:hover': { background: `${C.blue}18` },
                  }}>
                  <Typography sx={{ fontFamily: F.body, fontSize: 11, color: C.blue, fontWeight: 600 }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </AnimatePresence>

      {/* ── Floating button ── */}
      <Box component={motion.div} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }} sx={{ position: 'relative' }}>
        {!open && (
          <Box
            component={motion.div}
            animate={{ scale: [1, 1.55], opacity: [0.45, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
            sx={{ position: 'absolute', inset: 0, borderRadius: '50%', background: C.blue, pointerEvents: 'none' }}
          />
        )}
        <Box
          onClick={() => open ? setOpen(false) : openChat()}
          sx={{
            width: 60, height: 60, borderRadius: '50%',
            background: G.navyBlue, boxShadow: SH.btnBlue,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', overflow: 'hidden',
            border: `2px solid rgba(255,255,255,0.18)`,
          }}
        >
          <AnimatePresence mode="wait">
            {open
              ? <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <CloseRoundedIcon sx={{ color: C.white, fontSize: 26 }} />
                </motion.div>
              : <motion.div key="lion" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <LionAvatar size={52} />
                </motion.div>
            }
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatWidget;
